/**
 * Focus groups per screen (plan §6 M2-18): the screens render their focusable items with these group ids and
 * indices, and the focus model (`../nav.ts`) moves over them. Station screens stack: tab bar, the tab's groups,
 * notices, footer (Launch). Pure.
 */
import type { FocusGroup } from '../nav'
import type { StationTab } from '../contracts'

export const G = Object.freeze({
  tabs: 'tabs',
  notices: 'notices',
  footer: 'footer',
  sell: 'sell',
  buy: 'buy',
  components: 'components',
  /** The Market tab's Break down buttons beside the component rows (SY-3), in DOM order. */
  breakDown: 'breakDown',
  sellAll: 'sellAll',
  slots: 'slots',
  offers: 'offers',
  /** The outfitting tab's Salvaged buttons (SY-5c), then its Recondition buttons (SY-5b), in DOM order. */
  salvaged: 'salvaged',
  service: 'service',
  lease: 'lease',
  board: 'board',
  active: 'active',
  manual: 'manual',
  auto: 'auto',
  importSave: 'import',
  title: 'title',
  load: 'load',
  fallback: 'fallback',
  loadBack: 'loadBack',
  confirm: 'confirm',
  moment: 'moment',
  /** The buttons of a mounted external tab (M3 R22f: the Reach's station services), in DOM order. */
  external: 'external',
  /** CL-6: the station's More drawer (the tabs that wait off the bar), in drawer order. */
  more: 'more',
})

/** Repair tab items, in order (the partial rows are steppers). */
export const REPAIR_ITEMS = ['repairAll', 'repairPartial', 'rearmAll', 'rearmPartial'] as const
export type RepairItem = (typeof REPAIR_ITEMS)[number]

/** Lease Office items: before ownership pay arrears and pay extra (a stepper), after it the insurance toggle. */
export type LeaseItem = 'arrears' | 'extra' | 'insurance'
export function leaseItems(owned: boolean): LeaseItem[] {
  return owned ? ['insurance'] : ['arrears', 'extra']
}

/** Buttons per row of the grids. */
export const ACTIVE_COLS = 2 // hand in, abandon
export const MANUAL_COLS = 3 // save, load, export
export const AUTO_COLS = 2 // load, export
export const LOAD_COLS = 2 // load, export

/** Title main menu items. */
export const TITLE_ITEMS = ['continue', 'new', 'load', 'import'] as const
export type TitleItem = (typeof TITLE_ITEMS)[number]

/** Item counts of a station tab's groups (from its view-model). */
export interface TabCounts {
  /**
   * `breakDown`: the Break down buttons (SY-3; absent = none). CL-4: `buyFirst` puts the Buy rows above the Sell rows
   * (the model's `firstSection`); `sellAllTop` puts Sell all (and Send tender) above the Sell rows while something
   * aboard sells here. Absent: Sell, Buy, then Sell all at the end, as before.
   */
  market?: { sell: number; buy: number; components: number; breakDown?: number; tender?: boolean; buyFirst?: boolean; sellAllTop?: boolean }
  /** `salvaged`: the Salvaged plus Recondition buttons (SY-5c; absent = none). */
  outfitting?: { offers: number; salvaged?: number }
  lease?: { owned: boolean }
  contracts?: { board: number; active: number }
}

function all(n: number): number[] {
  return Array.from({ length: n }, (_, i) => i)
}

/** The tab's own groups. */
export function tabGroups(tab: StationTab, counts: TabCounts): FocusGroup[] {
  switch (tab) {
    case 'market': {
      const m = counts.market ?? { sell: 0, buy: 0, components: 0 }
      // SY-3: the Break down buttons sit beside the component rows (right from a row, left back) and below them in
      // the list; empty, the group passes moves through as before.
      const breakDown = m.breakDown ?? 0
      // M10 P5a3c: with the tender strip shown, Send tender (index 0, above) then Sell all (index 1).
      const sellAll: FocusGroup = { id: G.sellAll, count: m.tender === true ? 2 : 1 }
      const sell: FocusGroup[] = m.sellAllTop === true ? [sellAll, { id: G.sell, count: m.sell, steppers: all(m.sell) }] : [{ id: G.sell, count: m.sell, steppers: all(m.sell) }]
      const buy: FocusGroup = { id: G.buy, count: m.buy, steppers: all(m.buy) }
      // CL-4: the focus order follows the screen: the first section's rows, then the other's.
      return [
        ...(m.buyFirst === true ? [buy, ...sell] : [...sell, buy]),
        breakDown > 0 ? { id: G.components, count: m.components, right: G.breakDown } : { id: G.components, count: m.components },
        breakDown > 0 ? { id: G.breakDown, count: breakDown, left: G.components } : { id: G.breakDown, count: 0 },
        ...(m.sellAllTop === true ? [] : [sellAll]),
      ]
    }
    case 'outfitting': {
      // SY-5c: the Salvaged group sits below the slots (up returns to them); empty, it passes moves through as before.
      const salvaged = counts.outfitting?.salvaged ?? 0
      return [
        { id: G.slots, count: 9, right: G.offers, down: G.salvaged },
        { id: G.offers, count: counts.outfitting?.offers ?? 0, left: G.slots, up: G.tabs },
        salvaged > 0 ? { id: G.salvaged, count: salvaged, up: G.slots } : { id: G.salvaged, count: 0 },
      ]
    }
    case 'repair':
      return [{ id: G.service, count: REPAIR_ITEMS.length, steppers: [1, 3] }]
    case 'lease': {
      const items = leaseItems(counts.lease?.owned ?? false)
      return [{ id: G.lease, count: items.length, steppers: items.flatMap((it, i) => (it === 'extra' ? [i] : [])) }]
    }
    case 'contracts': {
      const c = counts.contracts ?? { board: 0, active: 0 }
      return [
        { id: G.board, count: c.board },
        { id: G.active, count: c.active * ACTIVE_COLS, cols: ACTIVE_COLS },
      ]
    }
    case 'saves':
      return [
        { id: G.manual, count: 3 * MANUAL_COLS, cols: MANUAL_COLS },
        { id: G.auto, count: 3 * AUTO_COLS, cols: AUTO_COLS },
        { id: G.importSave, count: 1 },
      ]
  }
}

/** The whole station screen: tab bar (CL-6: the primary tabs plus the More button), the tab's groups, notices, footer. */
export function stationGroups(tabCount: number, tab: StationTab, counts: TabCounts, notices: number): FocusGroup[] {
  return [
    { id: G.tabs, count: tabCount, cols: Math.max(1, tabCount) },
    ...tabGroups(tab, counts),
    { id: G.notices, count: notices },
    { id: G.footer, count: 1 },
  ]
}

/** The station screen while an external tab shows: tab bar (M2's tabs, then the external ones), its buttons, notices, footer. */
export function externalStationGroups(tabCount: number, buttons: number, notices: number): FocusGroup[] {
  return [
    { id: G.tabs, count: tabCount, cols: Math.max(1, tabCount) },
    { id: G.external, count: buttons },
    { id: G.notices, count: notices },
    { id: G.footer, count: 1 },
  ]
}

/**
 * CL-6: the station screen while the More drawer is open: the bar (its primary tabs and the More button) above the
 * drawer's tabs. The drawer takes the focus; notices and the footer wait until it closes.
 */
export function moreDrawerGroups(barCount: number, moreCount: number): FocusGroup[] {
  return [
    { id: G.tabs, count: barCount, cols: Math.max(1, barCount), down: G.more },
    { id: G.more, count: moreCount, up: G.tabs },
  ]
}

export type TitleView = 'main' | 'load'

export function titleGroups(view: TitleView, slots: number, fallback: boolean): FocusGroup[] {
  if (view === 'main') return [{ id: G.title, count: TITLE_ITEMS.length }]
  return [
    { id: G.load, count: slots * LOAD_COLS, cols: LOAD_COLS },
    { id: G.fallback, count: fallback ? 1 : 0 },
    { id: G.loadBack, count: 1 },
  ]
}

export function confirmGroups(): FocusGroup[] {
  return [{ id: G.confirm, count: 2, cols: 2 }]
}

export function momentGroups(): FocusGroup[] {
  return [{ id: G.moment, count: 1 }]
}
